import { PDFDocument, rgb, StandardFonts } from "pdf-lib";
import { addPreviewWatermarkPdfLib } from "./previewWatermark";
import { getFieldPositions, getTemplateUrl } from "./scheduleCGenerator";
import { resolveTaxLayout } from "./taxLayouts";
import { loadPdfJs } from "./pdfEngines";

// Set up pdf.js worker using unpkg CDN with correct version

// Convert PDF bytes to image using pdf.js
async function convertPdfToImage(pdfBytes) {
  const loadingTask = (await loadPdfJs()).getDocument({ data: pdfBytes });
  const pdf = await loadingTask.promise;
  
  // Get the first page
  const page = await pdf.getPage(1);
  
  // Set scale for good quality preview
  const scale = 2;
  const viewport = page.getViewport({ scale });
  
  // Create a canvas to render the PDF page
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  canvas.width = viewport.width;
  canvas.height = viewport.height;
  
  // Render the page to canvas
  await page.render({
    canvasContext: context,
    viewport: viewport
  }).promise;
  
  // Convert canvas to image data URL
  const imageDataUrl = canvas.toDataURL('image/png', 0.9);
  
  return imageDataUrl;
}

// Format currency without dollar sign
const formatCurrency = (amount) => {
  const num = parseFloat(amount) || 0;
  if (num === 0) return "";
  return num.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
};

// Template URL based on tax year

// Field positions matching main generator


// Generate Schedule C preview with watermark
export const generateScheduleCPreview = async (formData, taxYear) => {
  try {
    const layout = await resolveTaxLayout("schedule-c", taxYear, getFieldPositions(taxYear), getTemplateUrl(taxYear));
    const templateResponse = await fetch(layout.templateUrl);
    const templateBytes = await templateResponse.arrayBuffer();
    
    const pdfDoc = await PDFDocument.load(templateBytes);
    const pages = pdfDoc.getPages();
    const page = pages[0];
    const { width, height } = page.getSize();
    
    const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
    const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
    
    const positions = layout.positions;
    
    const drawText = (text, position, useBold = false) => {
      if (!text && text !== 0) return;
      if (Array.isArray(position)) {
        // a run of boxes (business code, EIN): one character per box,
        // separators like the EIN's dash dropped
        String(text).replace(/[^0-9A-Za-z]/g, "").split("").forEach((ch, i) => {
          if (position[i]) drawText(ch, position[i], useBold);
        });
        return;
      }
      const textStr = String(text);
      const selectedFont = useBold ? boldFont : font;
      const fontSize = position.fontSize || 10;
      
      let xPos = position.x;
      
      if (position.align === 'right' && position.width) {
        const textWidth = selectedFont.widthOfTextAtSize(textStr, fontSize);
        xPos = position.x + position.width - textWidth;
      }
      
      page.drawText(textStr, {
        x: xPos,
        y: position.y,
        size: fontSize,
        font: selectedFont,
        color: rgb(0, 0, 0),
      });
    };
    
    // Fill all fields (same as main generator)
    drawText(formData.proprietorName, positions.proprietorName, true);
    drawText(formData.ssn, positions.ssn);
    drawText(formData.principalBusiness, positions.principalBusiness);
    drawText(formData.businessCode, positions.businessCode);
    drawText(formData.businessName, positions.businessName);
    drawText(formData.ein, positions.ein);
    drawText(formData.businessAddress, positions.businessAddress);
    
    const cityStateZip = [formData.businessCity, formData.businessState, formData.businessZip].filter(Boolean).join(", ");
    drawText(cityStateZip, positions.businessCityStateZip);
    
    // The admin's layout editor sets __showAll to mark every choice at once
    // (both Yes and No, every method) while placing them
    const all = !!formData.__showAll;
    const is = (field, value) => all || formData[field] === value;
    if (is('accountingMethod', 'cash')) drawText('X', positions.cashMethod, true);
    if (is('accountingMethod', 'accrual')) drawText('X', positions.accrualMethod, true);
    if (is('accountingMethod', 'other')) {
      drawText('X', positions.otherMethod, true);
      drawText(formData.otherMethodText, positions.otherMethodText);
    }
    
    if (is('materialParticipation', 'yes')) drawText('X', positions.materialParticipationYes, true);
    if (is('materialParticipation', 'no')) drawText('X', positions.materialParticipationNo, true);
    if (all || formData.startedAcquired) drawText('X', positions.startedAcquired, true);
    if (is('payments1099', 'yes')) drawText('X', positions.payments1099Yes, true);
    if (is('payments1099', 'no')) drawText('X', positions.payments1099No, true);
    if (is('filed1099', 'yes')) drawText('X', positions.filed1099Yes, true);
    if (is('filed1099', 'no')) drawText('X', positions.filed1099No, true);
    
    // Part I - Income
    drawText(formatCurrency(formData.line1), positions.line1);
    if (formData.line1Statutory) drawText('X', positions.line1Checkbox, true);
    drawText(formatCurrency(formData.line2), positions.line2);
    drawText(formatCurrency(formData.line3), positions.line3);
    drawText(formatCurrency(formData.line4), positions.line4);
    drawText(formatCurrency(formData.line5), positions.line5);
    drawText(formatCurrency(formData.line6), positions.line6);
    drawText(formatCurrency(formData.line7), positions.line7);
    
    // Part II - Expenses
    drawText(formatCurrency(formData.line8), positions.line8);
    drawText(formatCurrency(formData.line9), positions.line9);
    drawText(formatCurrency(formData.line10), positions.line10);
    drawText(formatCurrency(formData.line11), positions.line11);
    drawText(formatCurrency(formData.line12), positions.line12);
    drawText(formatCurrency(formData.line13), positions.line13);
    drawText(formatCurrency(formData.line14), positions.line14);
    drawText(formatCurrency(formData.line15a), positions.line15a);
    drawText(formatCurrency(formData.line15b), positions.line15b);
    drawText(formatCurrency(formData.line16a), positions.line16a);
    drawText(formatCurrency(formData.line16b), positions.line16b);
    drawText(formatCurrency(formData.line17), positions.line17);
    drawText(formatCurrency(formData.line18), positions.line18);
    drawText(formatCurrency(formData.line19a), positions.line19a);
    drawText(formatCurrency(formData.line19b), positions.line19b);
    drawText(formatCurrency(formData.line20), positions.line20);
    drawText(formatCurrency(formData.line21), positions.line21);
    drawText(formatCurrency(formData.line22), positions.line22);
    drawText(formatCurrency(formData.line23a), positions.line23a);
    drawText(formatCurrency(formData.line23b), positions.line23b);
    drawText(formatCurrency(formData.line24), positions.line24);
    drawText(formatCurrency(formData.line25), positions.line25);
    drawText(formatCurrency(formData.line26a), positions.line26a);
    drawText(formatCurrency(formData.line26b), positions.line26b);
    
    // Totals
    drawText(formatCurrency(formData.line27), positions.line27);
    drawText(formatCurrency(formData.line28), positions.line28);
    drawText(formatCurrency(formData.line29), positions.line29);
    drawText(formatCurrency(formData.line30), positions.line30);
    drawText(formatCurrency(formData.line31), positions.line31);
    
    if (is('line32', 'a')) drawText('X', positions.line32a, true);
    if (is('line32', 'b')) drawText('X', positions.line32b, true);
    
    await addPreviewWatermarkPdfLib(pdfDoc, boldFont);
    
    const pdfBytes = await pdfDoc.save();
    
    // Convert PDF to image for preview display
    const imageDataUrl = await convertPdfToImage(pdfBytes);
    
    return imageDataUrl;
    
  } catch (error) {
    console.error("Error generating Schedule C preview:", error);
    throw error;
  }
};
